import { Controller } from "@hotwired/stimulus"

// "Edit" next to the project numbers in the Funds heading (Fund management,
// Phelipe 2026-09-29): a pop-up for just Project # and the two numbers the
// company named in Settings → Finances → Dashboard. Saves without reloading
// and redraws the numbers in place, each with "+N" and the rest on hover.
export default class extends Controller {
  static targets = ["chips", "modal", "number", "dfcm", "index", "error", "saveButton"]

  static values = {
    url: String,
    projectLabel: String,
    dfcmLabel: String,
    indexLabel: String,
    i18n: Object
  }

  open() {
    this._setError("")
    this.modalTarget.classList.remove("hidden")
    setTimeout(() => this.numberTarget.focus(), 30)
  }

  close() {
    this.modalTarget.classList.add("hidden")
    this._restoreInputs()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async save() {
    if (this.saving) return
    const project = { number: this.numberTarget.value }
    if (this.hasDfcmTarget) project.dfcm_number = this.dfcmTarget.value
    if (this.hasIndexTarget) project.fund_index_numbers = this.indexTarget.value

    this.saving = true
    this.saveButtonTarget.disabled = true
    try {
      const resp = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ project })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.success) {
        this._setError(data.error || this.i18nValue.error)
        return
      }
      this._saved = { number: data.number, dfcm: data.dfcm_number, index: data.fund_index_numbers }
      this._restoreInputs()
      this._redraw(data)
      this.modalTarget.classList.add("hidden")
      this._showToast(this.i18nValue.saved)
    } catch {
      this._setError(this.i18nValue.error)
    } finally {
      this.saving = false
      this.saveButtonTarget.disabled = false
    }
  }

  // Cancel puts back what's saved, so the next open starts from it.
  _restoreInputs() {
    if (!this._saved) {
      this._saved = {
        number: this.numberTarget.defaultValue,
        dfcm: this.hasDfcmTarget ? this.dfcmTarget.defaultValue : "",
        index: this.hasIndexTarget ? this.indexTarget.defaultValue : ""
      }
    }
    this.numberTarget.value = this._saved.number
    if (this.hasDfcmTarget) this.dfcmTarget.value = this._saved.dfcm
    if (this.hasIndexTarget) this.indexTarget.value = this._saved.index
  }

  _redraw(data) {
    const chips = this.chipsTarget
    chips.replaceChildren()
    if (data.number) {
      const wrap = document.createElement("span")
      wrap.append(document.createTextNode(`${this.projectLabelValue} `))
      const value = document.createElement("span")
      value.className = "font-medium text-gray-700"
      value.textContent = data.number
      wrap.append(value)
      chips.append(wrap)
    }
    // First number, then second — the order of Settings → Finances → Dashboard.
    if (this.indexLabelValue) chips.append(this._chip(this.indexLabelValue, data.index || []))
    if (this.dfcmLabelValue) chips.append(this._chip(this.dfcmLabelValue, data.dfcm || []))
  }

  // Same markup as finances/fund_management/_number_chip.html.erb.
  _chip(label, values) {
    const make = (tag, className, text) => {
      const node = document.createElement(tag)
      if (className) node.className = className
      if (text !== undefined) node.textContent = text
      return node
    }
    const wrap = make("span", "relative group inline-flex items-center gap-1.5")
    wrap.append(document.createTextNode(label))
    wrap.append(make("span", "font-medium text-gray-700", values[0] || "—"))
    if (values.length > 1) {
      const more = make("button", "inline-flex items-center h-5 px-1.5 rounded-full border border-gray-300 bg-white text-[13px] font-semibold text-brand-700 cursor-default", `+${values.length - 1}`)
      more.type = "button"
      more.setAttribute("aria-label", (this.i18nValue.show_all || "%{name}").replace("%{name}", label))
      const pop = make("span", "hidden group-hover:block group-focus-within:block absolute right-0 top-full mt-1 z-30 w-56 bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-2")
      pop.append(make("span", "block text-[13px] font-semibold text-gray-700 mb-1", label))
      values.forEach((value, i) => {
        const row = make("span", "flex items-center justify-between text-[13px] py-0.5")
        row.append(make("span", "font-medium text-gray-900", value))
        if (i === 0) row.append(make("span", "text-gray-500", this.i18nValue.main))
        pop.append(row)
      })
      wrap.append(more, pop)
    }
    return wrap
  }

  _setError(message) {
    this.errorTarget.textContent = message || ""
    this.errorTarget.classList.toggle("hidden", !message)
  }

  _showToast(message) {
    const el = document.createElement("div")
    el.className = "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.append(iconDiv, msg)
    document.body.prepend(el)
  }
}
